import {
    FiAlertTriangle,
    FiCheck,
    FiExternalLink,
    FiLock,
    FiMonitor,
    FiShield,
    FiSmartphone,
    FiWifi,
    FiZap,
} from "react-icons/fi";
import { useTranslation } from "../hooks/useTranslation";

function getWalletDebugSnapshot() {
    if (typeof window === "undefined") return [];
    const safeKeys = (value) => {
        try {
            return value && typeof value === "object" ? Object.keys(value).slice(0, 20).join(", ") : "";
        } catch (error) {
            return "unreadable";
        }
    };
    const providerFlags = (provider) => {
        if (!provider || typeof provider !== "object") return "missing";
        const flags = ["isMetaMask", "isTrust", "isTrustWallet", "isTokenPocket", "isCoinbaseWallet"]
            .filter((key) => Boolean(provider[key]))
            .join(", ");
        return flags || "no wallet flags";
    };
    const eth = window.ethereum;
    return [
        ["ua", navigator.userAgent || ""],
        ["href", window.location.href || ""],
        ["ethereum", eth ? `yes / request:${typeof eth.request} / on:${typeof eth.on} / ${providerFlags(eth)}` : "no"],
        ["ethereum.providers", Array.isArray(eth?.providers) ? String(eth.providers.length) : "no"],
        ["trustwallet", window.trustwallet ? `yes / keys:${safeKeys(window.trustwallet)}` : "no"],
        ["trustWallet", window.trustWallet ? `yes / keys:${safeKeys(window.trustWallet)}` : "no"],
        ["tokenpocket", window.tokenpocket ? `yes / keys:${safeKeys(window.tokenpocket)}` : "no"],
        ["intent", window.localStorage?.getItem("trustWalletBrowserIntentAt") || "none"],
    ];
}

function ProviderRow({ icon: Icon, name, description, badge, badgeTone = "ready" }) {
    const badgeClass = badgeTone === "warning"
        ? "border-amber-300/30 bg-amber-300/10 text-amber-100"
        : "border-[#a77cf4]/35 bg-[#9770DD]/15 text-[#dfd2fe]";

    return (
        <div className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.035] px-4 py-3 transition-colors hover:border-[#9770DD]/45 hover:bg-[#9770DD]/[0.08]">
            <div className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-[#9770DD]/25 bg-[#9770DD]/10 text-[#c7adff]">
                <Icon className="size-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white">{name}</div>
                <div className="mt-0.5 text-xs leading-relaxed text-white/55">{description}</div>
            </div>
            <div className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${badgeClass}`}>
                {badge}
            </div>
        </div>
    );
}

export default function WalletRequiredCard({
    variant = "install",
    onConnect,
    isConnecting = false,
    metaMaskDownloadUrl = "https://metamask.io/download/",
    trustWalletDeepLinkUrl,
    rememberTrustWalletIntent,
    walletDebugEnabled = false,
    walletConnectAvailable = false,
    walletConnectProjectIdMissing = false,
    isTokenPocketBlocked = false,
}) {
    const { t } = useTranslation();
    const isInstall = variant === "install";
    const walletDebugSnapshot = walletDebugEnabled ? getWalletDebugSnapshot() : [];
    const reownReady = walletConnectAvailable && !walletConnectProjectIdMissing;

    return (
        <section
            data-page="connect-wallet"
            data-production-provider="Reown AppKit"
            data-production-network="56"
            className="relative w-full max-w-3xl overflow-hidden rounded-[28px] border border-[#9770DD]/30 bg-[#0d0912]/95 shadow-2xl shadow-black/45"
        >
            <div className="pointer-events-none absolute -right-20 -top-24 size-64 rounded-full bg-[#9770DD]/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-20 size-64 rounded-full bg-[#f5c842]/10 blur-3xl" />

            <div className="relative grid gap-0 lg:grid-cols-[1.05fr_0.95fr]">
                <div className="border-b border-white/10 p-5 sm:p-8 lg:border-b-0 lg:border-r">
                    <div className="mb-6 flex items-center justify-between gap-3">
                        <div className="inline-flex items-center gap-2 rounded-full border border-[#9770DD]/30 bg-[#9770DD]/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#dfd2fe]">
                            <FiShield className="size-3.5" aria-hidden="true" />
                            Secure wallet access
                        </div>
                        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300">
                            <span className="size-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
                            BNB Ready
                        </div>
                    </div>

                    <div className="mb-7">
                        <div className="mb-5 flex size-16 items-center justify-center rounded-2xl border border-[#f5c842]/35 bg-gradient-to-br from-[#f5c842] to-[#b68d0c] text-[#171006] shadow-lg shadow-[#f5c842]/15">
                            <FiZap className="size-8" aria-hidden="true" />
                        </div>
                        <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">
                            Connect Wallet
                        </h1>
                        <p className="mt-3 max-w-md text-sm leading-6 text-white/65 sm:text-base">
                            Connect a compatible wallet to access your Pikachu dashboard, balances and on-chain actions.
                        </p>
                    </div>

                    <div className="rounded-2xl border border-[#f5c842]/20 bg-[#f5c842]/[0.07] p-4">
                        <div className="flex items-center gap-3">
                            <div className="flex size-10 items-center justify-center rounded-xl bg-[#f5c842] text-[#171006]">
                                <FiWifi className="size-5" aria-hidden="true" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#f5d76d]">Required network</div>
                                <div className="mt-0.5 text-base font-bold text-white">BNB Smart Chain</div>
                            </div>
                            <div className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-right">
                                <div className="text-[10px] uppercase tracking-widest text-white/40">Chain ID</div>
                                <div className="font-mono text-sm font-bold text-white">56</div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-5 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                        <FiLock className="mt-0.5 size-4 shrink-0 text-[#a98aef]" aria-hidden="true" />
                        <p className="text-xs leading-5 text-white/55">
                            This app never asks for your seed phrase. Your wallet provider controls approval, account access and network switching.
                        </p>
                    </div>
                </div>

                <div className="p-5 sm:p-8">
                    <div className="mb-4 flex items-end justify-between gap-3">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-[0.14em] text-white/40">Connection methods</div>
                            <h2 className="mt-1 text-xl font-bold text-white">Choose your wallet</h2>
                        </div>
                        <span className="text-xs text-white/35">Powered by Reown</span>
                    </div>

                    <div className="space-y-3">
                        <ProviderRow
                            icon={FiMonitor}
                            name="Browser wallet"
                            description="MetaMask or another compatible injected wallet."
                            badge={isInstall ? "Install" : "Detected"}
                            badgeTone={isInstall ? "warning" : "ready"}
                        />
                        <ProviderRow
                            icon={FiSmartphone}
                            name="WalletConnect"
                            description="Scan with a supported mobile wallet through Reown AppKit."
                            badge={reownReady ? "Available" : "Unavailable"}
                            badgeTone={reownReady ? "ready" : "warning"}
                        />
                    </div>

                    {isTokenPocketBlocked ? (
                        <div className="mt-4 flex gap-3 rounded-2xl border border-amber-300/25 bg-amber-300/10 p-4 text-amber-100">
                            <FiAlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                            <div>
                                <div className="text-sm font-bold">{t("header.tokenPocketNotSupportedTitle")}</div>
                                <div className="mt-1 text-xs leading-5 text-amber-100/70">{t("header.tokenPocketBlockedHint")}</div>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={onConnect}
                            disabled={isConnecting}
                            className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#f5c842] px-6 py-4 text-base font-black text-[#171006] shadow-lg shadow-[#f5c842]/15 transition-all hover:-translate-y-0.5 hover:bg-[#ffdc58] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <FiZap className="size-5" aria-hidden="true" />
                            {isConnecting ? t("common.loading") : t("header.connectWallet")}
                        </button>
                    )}

                    {isInstall && !isTokenPocketBlocked ? (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <a
                                href={trustWalletDeepLinkUrl || metaMaskDownloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={rememberTrustWalletIntent}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#9770DD]/35 bg-[#9770DD]/10 px-4 py-3 text-center text-xs font-bold text-[#dfd2fe] hover:bg-[#9770DD]/20"
                            >
                                Open Trust Wallet
                                <FiExternalLink className="size-3.5" aria-hidden="true" />
                            </a>
                            <a
                                href={metaMaskDownloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3 text-center text-xs font-bold text-white/75 hover:bg-white/[0.07]"
                            >
                                Install MetaMask
                                <FiExternalLink className="size-3.5" aria-hidden="true" />
                            </a>
                        </div>
                    ) : null}

                    <div className="mt-6 border-t border-white/10 pt-5">
                        <div className="grid grid-cols-3 gap-2">
                            {[
                                ["1", "Choose"],
                                ["2", "Approve"],
                                ["3", "Use dApp"],
                            ].map(([step, label]) => (
                                <div key={step} className="rounded-xl border border-white/10 bg-white/[0.025] px-2 py-3 text-center">
                                    <div className="mx-auto flex size-6 items-center justify-center rounded-full bg-[#9770DD]/20 text-[11px] font-black text-[#cebaff]">
                                        {step === "3" ? <FiCheck className="size-3.5" aria-hidden="true" /> : step}
                                    </div>
                                    <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-white/50">{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {walletConnectProjectIdMissing ? (
                        <p className="mt-4 text-center text-xs text-amber-200/80">
                            {t("header.walletConnectProjectIdMissing")}
                        </p>
                    ) : null}
                </div>
            </div>

            {walletDebugEnabled ? (
                <div className="relative border-t border-yellow-400/25 bg-gray-950/60 p-4 text-left">
                    <div className="text-xs font-semibold text-yellow-200">Wallet Debug</div>
                    <div className="mt-2 space-y-1 text-[11px] leading-relaxed text-white/70 break-words">
                        {walletDebugSnapshot.map(([label, value]) => (
                            <div key={label}><span className="text-white/90">{label}:</span> {String(value)}</div>
                        ))}
                    </div>
                </div>
            ) : null}
        </section>
    );
}
